import { useState, useCallback, useEffect, useRef } from "react";
import { PostStatus } from "@/core/types/postTypes";
import {
  clientService,
  ClientData,
  PostUpdateRequest,
} from "../services/clientService";
import { MediaItemData } from "@/core/MediaItem/MediaItem.model";
import { markPortalApproval } from "../push/approvalSignal";

interface UseApprovalDataOptions {
  clientId: string;
  token?: string;
  stage?: string;
  contentType?: 'fotos' | 'reels' | 'stories';
  onError?: (error: Error) => void;
  autoFetch?: boolean;
}

export const useApprovalData = ({
  clientId,
  token,
  stage,
  contentType,
  onError,
  autoFetch = true,
}: UseApprovalDataOptions) => {
  const [approvalData, setApprovalData] = useState<ClientData | null>(null);
  const [pendingMedia, setPendingMedia] = useState<MediaItemData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Track mounted state to prevent updates after unmount
  const isMountedRef = useRef(true);

  // Track in-flight requests to prevent race conditions
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const handleError = useCallback(
    (err: unknown, fallbackMessage: string) => {
      const error = err instanceof Error ? err : new Error(fallbackMessage);
      if (isMountedRef.current) {
        setError(error);
        onError?.(error);
      }
      return error;
    },
    [onError],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch initial data
  useEffect(() => {
    if (!autoFetch || !clientId || !token) {
      setIsInitialLoading(false);
      return;
    }

    const fetchInitialData = async () => {
      // Cancel any pending requests
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();

      setIsInitialLoading(true);
      setError(null);

      try {
        //Fetch posts and media data in parallel
        const [postsData, mediaResponse] = await Promise.all([
          clientService.getClientData(clientId),
          clientService.getPendingMedia(clientId, stage, 'client', contentType, 1),
        ]);

        console.log('[useApprovalData] Fetched media:', mediaResponse.mediaItems?.length || 0, 'items', contentType ? `(filtered by ${contentType})` : '', `page ${mediaResponse.page}/${mediaResponse.totalPages}`);

        if (isMountedRef.current) {
          setApprovalData(postsData);
          setPendingMedia(mediaResponse.mediaItems); // Replace on initial load
          setTotalCount(mediaResponse.totalCount);
          setTotalPages(mediaResponse.totalPages);
          setCurrentPage(mediaResponse.page);
          setHasNext(mediaResponse.hasNext);
          setHasPrev(mediaResponse.hasPrev);
        }
      } catch (err) {
        // Don't set error if request was aborted (component unmounted or deps changed)
        if (err instanceof Error && err.name !== "AbortError") {
          handleError(err, "Failed to fetch initial approval data");
        }
      } finally {
        if (isMountedRef.current) {
          setIsInitialLoading(false);
        }
      }
    };

    fetchInitialData();
  }, [clientId, token, stage, contentType, handleError, autoFetch]);

  const fetchPosts = useCallback(async () => {
    if (!clientId || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const postsData = await clientService.getClientData(
        clientId,
      );
      if (isMountedRef.current) {
        setApprovalData(postsData);
      }
    } catch (err) {
      handleError(err, "Failed to load posts");
      throw err; // Re-throw so callers can handle it
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [clientId, token, handleError]);

  const fetchMedia = useCallback(async () => {
    if (!clientId || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const mediaResponse = await clientService.getPendingMedia(
        clientId,
        stage,
        'client',
        contentType,
        1, // Reset to page 1
      );
      console.log('[useApprovalData] fetchMedia - received:', mediaResponse.mediaItems?.length || 0, 'items', contentType ? `(filtered by ${contentType})` : '');
      if (isMountedRef.current) {
        setPendingMedia(mediaResponse.mediaItems);
        setTotalCount(mediaResponse.totalCount);
        setTotalPages(mediaResponse.totalPages);
        setCurrentPage(mediaResponse.page);
        setHasNext(mediaResponse.hasNext);
        setHasPrev(mediaResponse.hasPrev);
      }
    } catch (err) {
      handleError(err, "Failed to load media");
      throw err;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [clientId, token, stage, contentType, handleError]);

  const loadMoreMedia = useCallback(async () => {
    if (!clientId || !token || !hasNext) return;

    setIsLoading(true);

    try {
      const nextPage = currentPage + 1;
      const mediaResponse = await clientService.getPendingMedia(
        clientId,
        stage,
        'client',
        contentType,
        nextPage,
      );
      console.log('[useApprovalData] loadMoreMedia - received:', mediaResponse.mediaItems?.length || 0, 'more items');
      if (isMountedRef.current) {
        setPendingMedia(prev => [...prev, ...mediaResponse.mediaItems]); // Append items
        setCurrentPage(mediaResponse.page);
        setHasNext(mediaResponse.hasNext);
        setHasPrev(mediaResponse.hasPrev);
      }
    } catch (err) {
      handleError(err, "Failed to load more media");
      throw err;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [clientId, token, stage, contentType, currentPage, hasNext, handleError]);

  const updatePost = useCallback(
    async (postId: string, updates: PostUpdateRequest) => {
      try {
        await clientService.updatePost(clientId, postId, updates);

        // Fetch fresh data instead of optimistic update to avoid type issues
        await fetchPosts();
      } catch (err) {
        handleError(err, "Failed to update post");
        throw err;
      }
    },
    [clientId, fetchPosts, handleError],
  );

  const reviewPost = useCallback(
    async (postId: string, isReviewed: boolean) => {
      try {
        if (isReviewed) {
          await clientService.reviewPost(clientId, postId);
          // Value moment reached: allow the post-approval permission ask.
          markPortalApproval();
        } else {
          await clientService.unreviewPost(clientId, postId);
        }
        await fetchPosts();
      } catch (err) {
        handleError(err, "Failed to update post review");
        throw err;
      }
    },
    [clientId, fetchPosts, handleError],
  );

  const rateMedia = useCallback(
    async (mediaId: number, rating?: number, comment?: string, skipRefetch?: boolean) => {
      try {
        await clientService.rateMedia(clientId, mediaId, rating, comment);
        // Value moment reached: allow the post-approval permission ask.
        markPortalApproval();
        // Patch local state so the rating shows immediately (reviewed flag,
        // stars) without a refetch. The review carousel eagerly loads several
        // pages of pending media; a page-1 refetch would drop the later pages
        // and reset the client's position, so callers pass skipRefetch there.
        setPendingMedia((prev) =>
          prev.map((item) =>
            item.id === mediaId
              ? {
                  ...item,
                  clientRating: rating ?? item.clientRating,
                  clientComment:
                    comment !== undefined ? comment : item.clientComment,
                  clientReviewedAt: new Date().toISOString(),
                }
              : item,
          ),
        );
        if (!skipRefetch) {
          await fetchMedia();
        }
      } catch (err) {
        handleError(err, "Failed to rate media");
        throw err;
      }
    },
    [clientId, fetchMedia, handleError],
  );

  const updateMediaLabel = useCallback(
    async (mediaId: number, label: string) => {
      try {
        await clientService.updateMediaLabel(
          clientId,
          mediaId,
          label,
        );
        await fetchMedia();
      } catch (err) {
        handleError(err, "Failed to update media label");
        throw err;
      }
    },
    [clientId, fetchMedia, handleError],
  );

  // Memoize derived state
  const pendingPosts =
    approvalData?.posts.filter(
      (post) => post.status === PostStatus.APPROVAL_PENDING,
    ) || [];

  const approvedPosts =
    approvalData?.posts.filter(
      (post) => post.status !== PostStatus.APPROVAL_PENDING,
    ) || [];

  return {
    // Data
    approvalData,
    pendingMedia,
    pendingPosts,
    approvedPosts,

    // Loading states
    isLoading,
    isInitialLoading,

    // Error handling
    error,
    clearError,

    // Actions
    fetchPosts,
    fetchMedia,
    loadMoreMedia,
    updatePost,
    reviewPost,
    rateMedia,
    updateMediaLabel,

    // Pagination
    hasNext,
    totalCount,
    currentPage,
    totalPages,
  };
};
